"use client";
import { useState } from 'react';
import confetti from 'canvas-confetti';

const COUPONS = {
  'CLEAN10': 10,
  'FIRSTORDER': 15
};

export default function CouponComponent({ onCouponApplied, onCouponRemoved }) {
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const triggerConfetti = () => {
    confetti({
      particleCount: 100,
      spread: 70,
      origin: { y: 0.6 }
    });
  };

  const handleCouponApply = async () => {
    const cleanCode = couponCode.trim().toUpperCase();

    if (!cleanCode) {
      setErrorMessage('Please enter a coupon code 💌');
      return;
    }

    if (COUPONS[cleanCode]) {
      setIsLoading(true);
      setErrorMessage('');

      await new Promise((resolve) => setTimeout(resolve, 1000));

      triggerConfetti();

      setAppliedCoupon(cleanCode);
      setSuccessMessage(`🎉 Woohoo! ${COUPONS[cleanCode]}% discount applied!`);
      setCouponCode('');
      setIsLoading(false);
      onCouponApplied(COUPONS[cleanCode]);
    } else {
      setErrorMessage('Oops! Invalid coupon code 😢');
      setSuccessMessage('');
    }
  };

  const removeCoupon = () => {
    setAppliedCoupon(null);
    setSuccessMessage('');
    setErrorMessage('');
    setCouponCode('');
    onCouponRemoved();
  };

  return (
    <div className="max-w-md mx-auto mb-16" data-aos="fade-up">
      <div className="flex flex-col gap-4 items-center">
        <div className="w-full relative group">
          <div className="absolute -inset-1 bg-gradient-to-r from-primary to-secondary rounded-xl opacity-20 blur-sm group-hover:opacity-30 transition-all"></div>
          <div className="relative">
            <input
              type="text"
              placeholder="🎁 Enter coupon code..."
              className="w-full px-6 py-4 border-2 border-light-border rounded-xl focus:outline-none focus:border-primary transition-all text-lg dark:bg-dark-surface dark:border-dark-border dark:text-dark-text-primary pr-4 disabled:bg-light-background/50 disabled:text-light-text-secondary"
              value={couponCode}
              onChange={(e) => {
                setCouponCode(e.target.value.toUpperCase());
                setErrorMessage('');
              }}
              onKeyPress={(e) => e.key === 'Enter' && !appliedCoupon && handleCouponApply()}
              disabled={!!appliedCoupon || isLoading}
            />
          </div>
        </div>

        <button
          onClick={appliedCoupon ? removeCoupon : handleCouponApply}
          className={`w-full py-3 px-6 rounded-xl font-semibold transition-all transform hover:scale-105 text-white shadow-lg hover:shadow-xl flex items-center justify-center gap-2 ${
            appliedCoupon ? 'bg-error hover:bg-error-hover' : 'bg-gradient-to-r from-primary to-secondary hover:from-primary-hover hover:to-secondary-hover'
          }`}
          disabled={(!couponCode.trim() && !appliedCoupon) || isLoading}
        >
          {isLoading ? (
            "Applying..."
          ) : appliedCoupon ? (
            <>
              Remove Coupon <span className="text-xl">✖</span>
            </>
          ) : (
            "Apply Coupon"
          )}
        </button>

        {/* Interactive Coupon Hint */}
        <div
          className="relative cursor-pointer group"
          onMouseEnter={(e) => {
            e.currentTarget.querySelector('.secret-coupon').classList.remove('hidden');
            e.currentTarget.querySelector('.question-mark').classList.add('hidden');
          }}
          onMouseLeave={(e) => {
            e.currentTarget.querySelector('.secret-coupon').classList.add('hidden');
            e.currentTarget.querySelector('.question-mark').classList.remove('hidden');
          }}
        >
          <div className="flex items-center gap-2 text-light-text-secondary dark:text-dark-text-secondary hover:text-primary transition-colors">
            <span className="question-mark">🔍 Looking for deals? Hover here!</span>
            <span className="secret-coupon hidden animate-bounce">Try: FIRSTORDER 🎁</span>
          </div>
        </div>

        {/* Animated Messages */}
        {errorMessage && (
          <div className="mt-4 w-full flex items-center gap-2 px-4 py-3 bg-error-light dark:bg-error-dark/20 rounded-xl animate-shake">
            <span>⚠️</span>
            <span className="text-error-dark dark:text-error">{errorMessage}</span>
            <button
              onClick={() => setErrorMessage('')}
              className="ml-auto text-error-dark hover:text-error-hover"
            >
              ×
            </button>
          </div>
        )}

        {successMessage && (
          <div className="mt-4 w-full flex items-center gap-2 px-4 py-3 bg-success-light dark:bg-success-dark/20 rounded-xl animate-pop-in">
            <span>🎉</span>
            <span className="text-success-dark dark:text-success">{successMessage}</span>
            <button
              onClick={() => {
                setSuccessMessage('');
              }}
              className="ml-auto text-success-dark hover:text-success-hover"
            >
              ×
            </button>
          </div>
        )}
      </div>
    </div>
  );
}